import { ButtonHTMLAttributes, useId, useState, useEffect } from "react";
import {
  IterateAndAddClass,
  IterateAndRemoveClass,
  IterateAndToggleClass,
  CONSTANTS,
} from "@util/utils";
import { viewport, colors, font } from "@styles/cssinjs/helpers/variables.js";

type announcementObject = {
  onState: string;
  offState: string;
};

type ToggleProps = {
  toggled: boolean;
  onToggleClick: (isToggled: boolean) => void;
  label: string;
  announcement?: announcementObject;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | "onClick"
  | "id"
  | "aria-checked"
  | "aria-labelledby"
  | "className"
  | "role"
  | "type"
>;

export const togglePlan = () => {
  const pricingWrapper = document.querySelector(
    ".pricing-container"
  ) as HTMLElement;
  const monthlyCost = document.querySelector(".monthly-cost") as HTMLElement;
  const annualCost = document.querySelector(".annual-cost") as HTMLElement;
  const pricingType = pricingWrapper ? pricingWrapper.id : "";

  const discountedPriceElements = [
    ...document.getElementsByClassName("discount-img"),
  ];
  const displayedPriceElements = [
    ...document.getElementsByClassName("price-wrapper"),
  ];

  const toggleBtn = [...document.getElementsByClassName("g-togg-btn")];

  const AnnualBillingSpans = [
    ...document.getElementsByClassName("annual-billing-span"),
  ];

  const MonthlyBillingSpans = [
    ...document.getElementsByClassName("monthly-billing-span"),
  ];

  if (pricingType === "annual-billing-plan") {
    IterateAndRemoveClass(discountedPriceElements, "active");
    IterateAndRemoveClass(displayedPriceElements, "counter");
    IterateAndToggleClass(toggleBtn, "g-togg--off", "g-togg--on");
    IterateAndRemoveClass(AnnualBillingSpans, "active");
    IterateAndAddClass(MonthlyBillingSpans, "active");
    monthlyCost?.classList.add("show");
    annualCost?.classList.remove("show");
    pricingWrapper.setAttribute("id", "monthly-billing-plan");

    // Update all plan buttons for monthly billing
    updatePlanButtons("monthly");
  } else {
    IterateAndAddClass(discountedPriceElements, "active");
    IterateAndAddClass(displayedPriceElements, "counter");
    IterateAndToggleClass(toggleBtn, "g-togg--on", "g-togg--off");
    IterateAndAddClass(AnnualBillingSpans, "active");
    IterateAndRemoveClass(MonthlyBillingSpans, "active");
    monthlyCost?.classList.remove("show");
    annualCost?.classList.add("show");
    if (pricingWrapper) {
      pricingWrapper.setAttribute("id", "annual-billing-plan");
    }

    // Update all plan buttons for annual billing
    updatePlanButtons("annual");
  }
};

// Function to update plan buttons based on selected plan type
const updatePlanButtons = (planType: "monthly" | "annual") => {
  const PRO_PLAN_IDS = {
    live: { monthly: "5524", annual: "5526" },
    staging: { monthly: "5504", annual: "5506" },
  } as const;

  const isLive = process.env.APP_MODE === "live";
  const url = isLive
    ? CONSTANTS.live.universalSignupUrl
    : CONSTANTS.staging.universalSignupUrl;

  // Pro-specific plan URLs (used by pro buttons)
  const planIds = isLive ? PRO_PLAN_IDS.live : PRO_PLAN_IDS.staging;
  const proUrls = {
    monthly: `${url}/signup?planId=${planIds.monthly}`,
    annual: `${url}/signup?planId=${planIds.annual}`,
  };

  // Define all button configurations
  const buttonConfigs = [
    {
      selector: ".pro-cta-header",
      urls: proUrls,
    },
    {
      selector: ".pro-cta-plan-button",
      urls: proUrls,
    },
    {
      selector: ".feature-get-pro-btn",
      urls: proUrls,
    },
    // Additional selectors from pricing page
    {
      selector: ".pro-cta-button",
      urls: proUrls,
    },
    {
      selector: ".pro-cta-footer",
      urls: proUrls,
    },
    {
      selector: ".pro-cta-footer-mobile",
      urls: proUrls,
    },
    {
      selector: ".pro-cta-mobile-footer",
      urls: proUrls,
    },
    {
      selector: ".pricing-pro-table",
      urls: proUrls,
    },
  ];

  // Update all buttons
  buttonConfigs.forEach((config) => {
    const button = document.querySelectorAll(config.selector);
    if (button) {
      button.forEach((btn) => {
        if (config.urls) {
          btn.setAttribute("href", config.urls[planType]);
        }
      });
    }
  });
};

export function ToggleButton({
  toggled = false,
  label = "toggle switch.",
  disabled = false,
  announcement,
  ...props
}: ToggleProps) {
  const [isChecked, setIschecked] = useState<boolean>(toggled);
  const id = useId();

  useEffect(() => {
    const syncFromDOM = () => {
      const pricingWrapper = document.querySelector(
        ".pricing-container"
      ) as HTMLElement;
      let planType: "monthly" | "annual";

      if (pricingWrapper) {
        const isAnnual = pricingWrapper.id === "annual-billing-plan";
        setIschecked(isAnnual);
        planType = isAnnual ? "annual" : "monthly";
      } else {
        setIschecked(toggled);
        planType = toggled ? "annual" : "monthly";
      }

      if (document.querySelector(".pricing-pro-table")) {
        updatePlanButtons(planType);
      }
    };

    syncFromDOM();

    const observer = new MutationObserver(syncFromDOM);
    const pricingWrapper = document.querySelector(".pricing-container");
    if (pricingWrapper) {
      observer.observe(pricingWrapper, {
        attributes: true,
        attributeFilter: ["id"],
      });
    }

    return () => observer.disconnect();
  }, [toggled]);

  const handleClicked = () => {
    togglePlan();
    const pricingWrapper = document.querySelector(
      ".pricing-container"
    ) as HTMLElement;
    if (pricingWrapper) {
      const isAnnual = pricingWrapper.id === "annual-billing-plan";
      setIschecked(isAnnual);
    } else {
      setIschecked(!isChecked);
    }
  };

  return (
    <button
      aria-checked={isChecked ? "true" : "false"}
      aria-label={label}
      aria-describedby={id}
      className={`g-togg-btn g-togg-btn-lg ${
        isChecked ? "g-togg--on" : "g-togg--off"
      }`}
      onClick={handleClicked}
      role="switch"
      type="button"
      {...props}
    >
      <cite></cite>
      <style jsx>{`
        .g-togg-btn {
          width: 38px;
          height: 20px;
          cursor: pointer;
          position: relative;
          padding: 2px;
          border-radius: 15px;
          background-color: ${colors.primitive.grey[1400]};
          border: none;
        }
        .g-togg-btn cite {
          width: 19px;
          height: 16px;
          position: absolute;
          top: 2px;
          left: 2px;
          font-size: ${font.size.sm[1]};
          font-style: normal;
          padding: 6px 7px 5px;
          border-radius: 13px;
          background-color: ${colors.primitive.white.default};
          transition: all 0.3s ease;
          text-align: center;
        }
        .g-togg-btn.g-togg-btn-lg {
          width: 46px;
          height: 26px;
        }
        .g-togg-btn.g-togg-btn-lg cite {
          height: 22px;
          width: 22px;
        }
        .g-togg-btn.g-togg--on {
          background-color: ${colors.primitive.green.light};
        }
        .g-togg-btn.g-togg--on cite {
          left: 21px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .g-togg--off {
            background-color: ${colors.primitive.black[300]};
          }
        }
      `}</style>
      {announcement && Object.keys(announcement).length > 0 && (
        <span id={id} className="sr-only">
          {isChecked ? announcement.onState : announcement.offState}
        </span>
      )}
    </button>
  );
}
